import React, { useCallback, Fragment, useEffect, useContext, useMemo, MouseEvent } from "react";
import { withRouter } from "react-router";
import cn from "classnames";
import moment from "moment";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { OppiziLogoSVG } from "components/icons/components/Oppizi";
import { EDDMLogo } from "components/icons/components/EDDMlogo";
import { useParams, useHistory } from "react-router-dom";
import { Anchor } from "components/Anchor";
import CopilotLauncher from "copilot/CopilotLauncher";
import { Tabs } from "shared/models";
import { EDDM_SIGNUP, CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import Box from "@material-ui/core/Box";
import { useTheme, useMediaQuery, makeStyles } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import CircularProgress from "@material-ui/core/CircularProgress";
import { usePostHog } from "posthog-js/react";
import { DialogModalContext } from "components/dialog-modal";
import { If, IfElse } from "components/logic";
import { LightTooltip } from "components/light-tooltip";
import { CustomTooltipContent } from "components/tooltip-content";
import CONFIG from "config/config";
import { CAMPAIGN_INTERNAL_STATUSES } from "pages/constants";
import backIcon from "static/media/back.svg";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import { FlyerStatus } from "pages/flyer/flyerTypePage/useGetDataForPage";
import { useStore } from "../../store";
import { StoreType } from "../../store/types";
import { authorization } from "../../module/auth";
import { CurrentStepIcon } from "../../static/media/current-step";
import { NextButton } from "./components";
import { Badge } from "./components/badge";
import style from "./style";
import { VerificationAlert } from "./components/VerificationAlert";
import { EddmStepBillingGate } from "../../campaign-builder/checkout/billing-profile/EddmStepBillingGate";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import KeyboardShortcutsModal from "components/KeyboardShortcuts/KeyboardShortcutsModal";
import { ArrowLeft } from "lucide-react";
import { ThemeToggle } from "campaign-builder/shell/ThemeToggle";
import { UserMenu } from "campaign-builder/channels/directmail/adm/UserMenu";
import { TickCircle } from "components/icons/components/TickCircle";

const useStyles = makeStyles((theme) => style(theme));

// DM (Direct Mail / EDDM submit) step-nav markers — mirror the redesigned
// StepHeader / EDDMHeader so the submit topbar reads identically to the other
// EDDM steps. Used ONLY by the `isDM` branch below; the legacy non-DM branch
// keeps its MUI CheckCircle/RadioButtonUnchecked icons untouched.
const FutureCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" stroke="#cbd5e1" strokeWidth="1.5" className="dark:stroke-slate-600" />
  </svg>
);

const CompletedStepIcon = () => (
  <TickCircle fill="#2E3860" size={22} className="dark:[&_path]:fill-slate-300 dark:[&_circle]:fill-slate-900" />
);

const Header: React.FC<any> = ({
  classes,
  isFlyerBuilderPage = false,
  flyerActions,
  tabs = [],
  activeTabIndex,
  hideBackButton,
  headerActions,
  isUploadPage = false,
  isSubmitted,
  hasError = false,
  isDM = false,
  isBackLoading = false,
}) => {
  const isClient = authorization.isClient();
  const runDialogModal = useContext(DialogModalContext);
  const posthog = usePostHog();
  const posthogEventTracker = new EmitAnalyticsEvent(posthog);
  const params = useParams();
  const history = useHistory();
  const { pageState, isQrCodeExist, isLoading, flyerInfo } = useGetFlyerInfo();
  const handleGoToTab = (href: string) => {
    const link = href.replace(":clientId", params.clientId).replace(":campaignId", params.campaignId).replace("?", "");
    history.push(link);
  };

  const currentTab = tabs.find((tab: Tabs) => tab.index === activeTabIndex);
  // Keyed off `step` (not the display title) so renamed EDDM labels
  // (Targeting/Design/Tracking) don't disable the submit-page verification alert.
  const isSubmitPage = currentTab?.step === "submit";

  const {
    user: { id: userId, clientId, firstName, lastName, email, accessDenied = true, phone, role, isVerified },
    client: { id: activeClientId, name: clientName, createdBy },
    campaign,
    costsCalculationData: { detailedCost },
    country,
    errors: { notExistDesignFile },
    distributionLocations,
  }: StoreType = useStore();
  const isEDDMsignup = createdBy === EDDM_SIGNUP;

  const { campaignName, id: campaignId, internalStatus, flyerType, flyerWeight, qrCodeLink, subtype } = campaign;

  const homeUrl = useMemo(() => `${CONFIG.PLATFORM_BASE_URL}${isClient ? `/dashboard/home` : ""}`, [isClient]);

  const postHogHeaderInfo = {
    user_id: userId,
    campaign_id: campaignId,
    company_id: clientId,
    cb_step: tabs.find((tab: Tabs) => tab.index === activeTabIndex)?.step,
  };

  // Help menu state (flyer builder only — anchor for the dropdown
  // and visibility of the keyboard shortcuts modal). On non-builder
  // pages the click goes straight to the original help-URL flow.
  const [helpMenuAnchor, setHelpMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = React.useState(false);

  const handleFAQClick = (e?: MouseEvent<HTMLElement>) => {
    posthog.capture("Clicked on CB header help button", postHogHeaderInfo);
    if (e?.currentTarget) setHelpMenuAnchor(e.currentTarget);
  };

  const closeHelpMenu = () => setHelpMenuAnchor(null);

  const handleStartTutorial = () => {
    closeHelpMenu();
    window.dispatchEvent(new CustomEvent("tour:start"));
  };

  const handleOpenShortcuts = () => {
    closeHelpMenu();
    setIsShortcutsModalOpen(true);
  };

  const handleHomeClick = (e: MouseEvent) => {
    posthog.capture("Clicked on CB home button", postHogHeaderInfo);
    const isCtrlClick = e.ctrlKey || e.metaKey;
    if (isCtrlClick) {
      return;
    } else if (!isCtrlClick && !campaign.isSubmitted) {
      runDialogModal({
        title: "Exit campaign creation?",
        contentText: (
          <Box style={{ padding: "10px 40px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div />
            <div style={{ textAlign: "center" }}>
              Are you sure you want to exit the campaign creation? Any unsaved progress will be lost.
            </div>
          </Box>
        ),
        ctaLabel: "Exit",
        handleCTAClick: () => {
          posthogEventTracker.run({
            eventName: "Exited CB through oppizi logo",
            userId: userId,
            clientName,
            device: navigator.userAgent,
            additionalPayload: { cbStep: currentTab?.title || "" },
          });
          window.location.href = homeUrl;
        },
        secondaryButtonLabel: "Cancel",
        hasOptions: true,
      });
    }
  };

  const flyerActionClick = useCallback(
    (item: any, e: any) => {
      if (posthog) {
        posthog.capture("Completed Flyer Building", {
          event_category: "Flyer Builder",
          campaign_qr_code: qrCodeLink || "",
          campaign_flyer_type: flyerType,
          campaign_flyer_weight: flyerWeight,
        });
      }
      if ((item?.variant && item.isActive) || item.title === "Home" || item.title === "Save") {
        item.action(e);
      }
    },
    [qrCodeLink, flyerType, flyerWeight]
  );

  const getTooltipContent = ({ isValidUrl = false, isQrCodeOnCanvas = false }) => {
    if (!isQrCodeOnCanvas) {
      return (
        <CustomTooltipContent
          title="QR code is outside the safe area!"
          content="Please, make sure the QR code is within the safe areas."
        />
      );
    }

    if (!isValidUrl) {
      return <CustomTooltipContent title="URL missing!" content="Please, provide a valid URL address." />;
    }

    return "";
  };

  const renderButtons = (item: any, index: number, isNext: boolean) => {
    if (item.component) {
      return item.component;
    }
    if (isNext && (!item.isValidUrl || !item.isQrCodeOnCanvas)) {
      return (
        <LightTooltip
          title={getTooltipContent({
            isValidUrl: item.isValidUrl,
            isQrCodeOnCanvas: item.isQrCodeOnCanvas,
          })}
          placement="bottom"
        >
          <span>
            <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
          </span>
        </LightTooltip>
      );
    } else {
      return item.href ? (
        <Anchor
          href={item.href}
          onClick={(e: MouseEvent) => {
            const isCtrlClick = e.ctrlKey || e.metaKey;
            if (!isCtrlClick && !campaign.isSubmitted) {
              e.preventDefault();
            }
          }}
        >
          <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
        </Anchor>
      ) : (
        <NextButton key={`${item.title}-${index}`} item={item} onClick={flyerActionClick} />
      );
    }
  };

  const handleBackClick = () => {
    posthog.capture("Clicked on CB back button", postHogHeaderInfo);
    headerActions?.BACK?.action();
  };

  useEffect(() => {
    if (posthog && userId) {
      posthog.identify(userId, {
        role,
        client_id: clientId,
        user_id: userId,
        firstname: firstName,
        lastname: lastName,
        country: country?.name,
        email,
        phone,
      });
    }
  }, [userId, role, clientId, firstName, lastName, country, email, phone, country]);

  const completedStep = tabs.find((item: any) => item.step === campaign.lastActiveStep);

  // Same correction as the EDDM topbar: `lastActiveStep` is a breadcrumb only
  // the wizard writes, and a campaign built through the API sits at "channel",
  // which is not one of these tabs — so the lookup above returns undefined and
  // every forward tab goes dead on a campaign that is already complete. Ask the
  // campaign what is true and take whichever measure is further along. This
  // only ever unlocks.
  const hasRoutesForNav = (distributionLocations ?? []).some(
    (location: any) => (location?.selectedRoutes ?? []).length > 0
  );
  const hasDesignForNav = !notExistDesignFile;
  const reachedIndexForNav = tabs.reduce((furthest: number, item: any) => {
    const satisfied =
      item.step === "segments" ||
      item.step === "channel" ||
      (item.step === "design" && hasRoutesForNav) ||
      (item.step === "details" && hasRoutesForNav) ||
      (item.step === "submit" && hasRoutesForNav && hasDesignForNav);
    return satisfied ? Math.max(furthest, item.index) : furthest;
  }, -1);
  const progressIndex = Math.max(completedStep?.index ?? -1, reachedIndexForNav);
  const disabledStepsByFlyerDesignNotExisting = notExistDesignFile ? ["details", "submit"] : [];
  const notVerified = isVerified === false;
  const isShowCosts = !isDM && subtype !== CAMPAIGN_SUBTYPES_ENUM.ADM;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1000));
  const headerWithSkipLogic = (isActive: boolean, canBeActive: boolean, item: any, forbidDetails: boolean) => {
    if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW && item.step === "submit" && !isActive) {
      return <CheckCircleIcon />;
    }

    if (forbidDetails && !isMobile) {
      return <RadioButtonUncheckedIcon color="disabled" />;
    }
    return (
      isMobile ||
      (isActive ? (
        <CurrentStepIcon />
      ) : progressIndex >= item.index ? (
        <CheckCircleIcon />
      ) : (
        <RadioButtonUncheckedIcon color={isActive || canBeActive ? "inherit" : "disabled"} />
      ))
    );
  };

  // DM (EDDM submit) topbar surface — mirrors EDDMHeader's `wrapperSurface`:
  // plain white with no shadow in light, the slate `card` token in dark.
  // `border-solid box-border` lets `border-*` utilities render past the global
  // preflight reset (see cb-v2 note in EDDMHeader).
  const dmWrapper = "relative top-0 z-[3] box-border h-auto w-full min-w-0 border-solid bg-white dark:bg-card";

  return (
    <>
      {/* Help dropdown + Keyboard Shortcuts modal hoisted to the
          outermost level so they work regardless of which render
          branch below ends up mounted (desktop vs mobile layouts have
          separate help buttons but both call `handleFAQClick`).
          The MUI Menu uses a portal anyway so its DOM position
          relative to the trigger doesn't matter — positioning is
          driven by `anchorEl`. */}
      <Menu
        anchorEl={helpMenuAnchor}
        open={Boolean(helpMenuAnchor)}
        onClose={closeHelpMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { borderRadius: 8, marginTop: 4 } }}
        MenuListProps={{ style: { padding: 4 } }}
      >
        <MenuItem className="!rounded-[6px] hover:!bg-[#E2E2FC] !gap-2" onClick={handleStartTutorial}>
          {/* Compass icon — matches the visual register of the
              back-button dropdown items used elsewhere. */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2D264B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          Tutorial
        </MenuItem>
        <MenuItem className="!rounded-[6px] hover:!bg-[#E2E2FC] !gap-2" onClick={handleOpenShortcuts}>
          {/* Keyboard icon — 18×18 to pair visually with the compass. */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2D264B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="6" width="20" height="12" rx="2" ry="2" />
            <line x1="6" y1="10" x2="6.01" y2="10" />
            <line x1="10" y1="10" x2="10.01" y2="10" />
            <line x1="14" y1="10" x2="14.01" y2="10" />
            <line x1="18" y1="10" x2="18.01" y2="10" />
            <line x1="6" y1="14" x2="18" y2="14" />
          </svg>
          Keyboard Shortcuts
        </MenuItem>
      </Menu>
      <KeyboardShortcutsModal isOpen={isShortcutsModalOpen} onClose={() => setIsShortcutsModalOpen(false)} />
      <IfElse condition={accessDenied}>
        <Box className={isDM ? dmWrapper : cn(classes.headerWrapper, { [classes.eddmHeaderWrapper]: isDM })} />
        <Box
          className={
            isDM
              ? cn(dmWrapper, "mob-header-wrapper")
              : cn(classes.headerWrapper, "mob-header-wrapper", {
                  [classes.eddmHeaderWrapper]: isDM,
                  [classes.notVerifiedWrapper]: notVerified,
                })
          }
        >
          {isSubmitPage && <VerificationAlert />}
          <IfElse condition={isFlyerBuilderPage}>
            <Box className={cn(classes.flyerContainer, { [classes.notVerifiedContainer]: notVerified })}>
              <div className={cn(classes.firstHeaderItem, { "!hidden": !flyerInfo })}>
                <Box className={classes.homeAndBackWrapper}>
                  <Anchor
                    href={homeUrl}
                    className={classes.homeBtn}
                    onClick={(e: MouseEvent) => {
                      const isCtrlClick = e.ctrlKey || e.metaKey;
                      if (!isCtrlClick && !campaign.isSubmitted) {
                        posthogEventTracker.run({
                          eventName: "Opened home on a new tab using oppizi logo",
                          userId: userId,
                          clientName,
                          device: navigator.userAgent,
                          additionalPayload: { cbStep: currentTab?.title || "" },
                        });
                        e.preventDefault();
                      }
                    }}
                  >
                    <OppiziLogoSVG onClick={handleHomeClick} />
                  </Anchor>
                  <Box
                    className={cn(classes.backBtn)}
                    onClick={handleBackClick}
                    // @ts-ignore
                    cypress_id={headerActions?.BACK?.cypressId}
                  >
                    {isBackLoading ? (
                      <CircularProgress size={20} style={{ color: "#64b5f6" }} />
                    ) : (
                      <>
                        <img src={backIcon} />
                        <Typography>Back</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </div>
              <div className={cn(classes.middleHeaderItem, { "!hidden": !flyerInfo })}>
                <Box className={classes.nameAndBadgeWrapper}>
                  <Typography className="text-center" data-cy={"campaignNameInTheHeader"}>
                    {campaignName}
                  </Typography>
                  {isShowCosts && <Badge totalClassName={classes.totalCost} />}
                </Box>
              </div>
              <div className={cn(classes.lastHeaderItem, { "!max-w-full flex justify-end": !flyerInfo })}>
                <Box style={{ display: "flex", justifyContent: "end", alignItems: "center", paddingRight: "24px" }}>
                  <Box className={cn(classes.flyerBuilderNavigationBtnsWrapper, "flex items-center justify-end")}>
                    {flyerActions?.map((item: any, index: number) =>
                      renderButtons(item, index, flyerActions.length - 1 === index)
                    )}
                  </Box>
                </Box>
              </div>
            </Box>
            {isDM ? (
              // DM (EDDM submit) step-nav — re-skinned to Tailwind aligned to the
              // redesigned StepHeader/EDDMHeader (tokenized light + dark). Mirrors
              // the EDDMHeader grid, back arrow (lucide ArrowLeft), step markers
              // (FutureCircle / CompletedStepIcon / CurrentStepIcon) and the
              // `h-px w-12` separators. Submit has no cost/Continue cluster or
              // ThemeToggle, so the right column is a spacer (was `navigationHelp`).
              <div
                className={cn(
                  "relative grid h-20 w-full grid-cols-12 [grid-template-rows:100%] gap-4 px-5",
                  "mob-grid-wrapper"
                )}
              >
                <div className={cn("col-[1/3] flex items-center", "home-back-icon mob-navigation-back-and-home")}>
                  <div className="mr-0 md:mr-4">
                    <If condition={!hideBackButton}>
                      <div
                        onClick={handleBackClick}
                        className={cn(
                          "flex w-fit cursor-pointer items-center text-[#222336] transition-all dark:text-foreground",
                          "mob-navigation-height"
                        )}
                        // @ts-ignore
                        cypress_id={headerActions?.BACK?.cypressId}
                      >
                        {isBackLoading ? (
                          <CircularProgress size={20} style={{ color: "#64b5f6" }} />
                        ) : (
                          <>
                            <ArrowLeft className="mr-1 h-5 w-5" />
                            <span className={cn("text-base font-bold leading-5", "mob-hidden")}>Back</span>
                          </>
                        )}
                      </div>
                    </If>
                    <If condition={hideBackButton}>
                      <div
                        onClick={handleBackClick}
                        className={cn(
                          "flex w-fit cursor-pointer items-center text-[#222336] transition-all dark:text-foreground",
                          "mob-navigation-height"
                        )}
                        // @ts-ignore
                        cypress_id={headerActions?.BACK?.cypressId}
                      >
                        <span />
                      </div>
                    </If>
                  </div>
                </div>
                <div className="col-[4/7] flex items-center justify-center min-[1100px]:col-[4/8] min-[1200px]:col-[5/8] min-[1400px]:col-[5/9]">
                  <div className={cn("flex h-full items-center justify-center gap-4", "mob-tabs-wrapper")}>
                    {tabs.map((item: any) => {
                      const isActive = activeTabIndex === item.index;
                      const nonActive = activeTabIndex !== item.index;
                      const checkProcessedChannel = progressIndex >= item.index;
                      //This is needed cus the user can go to create design and back that for some reason counts as creating the flyer
                      const forbidDetails =
                        (item.index >= 3 &&
                          !isDM &&
                          pageState === FlyerStatus.flyerCreated &&
                          !isQrCodeExist &&
                          !flyerInfo?.s3Link) ||
                        isLoading;
                      const isSubmitTabInReview =
                        internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW && item.step === "submit";
                      const canBeActive = !hasError && (checkProcessedChannel || isSubmitTabInReview);
                      const isLink = canBeActive && !isActive && !forbidDetails;
                      return (
                        <Fragment key={item.index}>
                          <span
                            // Anchor per wizard step, same as the EDDM topbar.
                            // This header renders the flyer/design pages, and
                            // without it the agent could reach Design and then
                            // had no addressable way back out.
                            data-cy={`campaignStep-${item.step}`}
                            aria-current={isActive ? "step" : undefined}
                            onClick={() => {
                              if (isLink) {
                                handleGoToTab(item.href);
                              }
                            }}
                            className={cn(
                              "flex h-full items-center gap-2 rounded-lg p-2.5",
                              "mob-item",
                              isLink && "cursor-pointer hover:h-11 hover:bg-[#E2E2FC] dark:hover:bg-accent",
                              { "display-none": nonActive, hidden: isMobile && nonActive }
                            )}
                          >
                            {!isMobile &&
                              (internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW &&
                              item.step === "submit" &&
                              !isActive ? (
                                <CompletedStepIcon />
                              ) : forbidDetails ? (
                                <FutureCircle />
                              ) : isActive ? (
                                <CurrentStepIcon width={22} height={22} />
                              ) : progressIndex >= item.index ? (
                                <CompletedStepIcon />
                              ) : (
                                <FutureCircle />
                              ))}
                            <div
                              key={`${item.title}-${item.index}`}
                              className={cn("flex items-center", {
                                "relative h-[90%] flex-col justify-around": isMobile,
                              })}
                            >
                              <span
                                className={cn(
                                  "text-sm",
                                  (isActive || canBeActive) && !forbidDetails
                                    ? isActive
                                      ? "font-semibold text-foreground"
                                      : "text-foreground"
                                    : "text-slate-400",
                                  isLink && "cursor-pointer"
                                )}
                              >
                                {item.title}
                              </span>
                              <DMStepDots totalSteps={tabs.length} currentStep={activeTabIndex} isMobile={isMobile} />
                            </div>
                          </span>
                          {item.index < tabs.length ? (
                            <div
                              key={`seperator-${item.index}`}
                              className={cn("h-px w-12 bg-slate-200 dark:bg-accent", { hidden: isMobile })}
                            />
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
                <div className="col-[10/13] flex items-center justify-end gap-2 pr-1">
                  <CopilotLauncher />
                  <ThemeToggle />
                  <UserMenu clientId={clientId} companyName={clientName} />
                </div>
              </div>
            ) : (
              <Box
                style={{ position: "relative" }}
                className={cn(classes.container, "mob-container", { [classes.notVerifiedContainer]: notVerified })}
              >
                <Grid container className={cn(classes.gridWrapper, "mob-grid-wrapper")}>
                  {/* Topbar logo removed: the only remaining consumers of this header
                    branch are EDDM flows, where the redesigned oads-sidebar owns the
                    logo. (Legacy non-DM flyer flow that used it has been removed.)
                    Only the Back arrow remains below. */}
                  <Box
                    className={cn(classes.newNavigationBtnsWrapper, {
                      [classes.newNavigationBtnsWrapperEDDMOnly]: isEDDMsignup,
                    })}
                  >
                    <If condition={!hideBackButton}>
                      <Box
                        onClick={handleBackClick}
                        className={cn(classes.backBtn, "mob-navigation-height")}
                        // @ts-ignore
                        cypress_id={headerActions?.BACK?.cypressId}
                      >
                        {isBackLoading ? (
                          <CircularProgress size={20} style={{ color: "#64b5f6" }} />
                        ) : (
                          <>
                            <img src={backIcon} style={{ marginBottom: 0, marginRight: 6 }} />
                            <Typography>Back</Typography>
                          </>
                        )}
                      </Box>
                    </If>
                    <If condition={hideBackButton}>
                      <Box
                        onClick={handleBackClick}
                        className={cn(classes.backBtn, "mob-navigation-height")}
                        // @ts-ignore
                        cypress_id={headerActions?.BACK?.cypressId}
                      >
                        <Typography></Typography>
                      </Box>
                    </If>
                    <Grid item className={classes.stepsContainer}>
                      <Box
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
                        className={cn("mob-tabs-wrapper")}
                      >
                        {tabs.map((item: any) => {
                          const isActive = activeTabIndex === item.index;
                          const nonActive = activeTabIndex !== item.index;
                          const errorDeletedDesign = disabledStepsByFlyerDesignNotExisting.includes(item.step);
                          const checkProcessedChannel = progressIndex >= item.index;
                          //This is needed cus the user can go to create design and back that for some reason counts as creating the flyer
                          const forbidDetails =
                            (item.index >= 3 &&
                              !isDM &&
                              pageState === FlyerStatus.flyerCreated &&
                              !isQrCodeExist &&
                              !flyerInfo?.s3Link) ||
                            isLoading;
                          const isSubmitTabInReview =
                            internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW && item.step === "submit";
                          const canBeActive = !hasError && (checkProcessedChannel || isSubmitTabInReview);
                          const isLink = canBeActive && !isActive && !forbidDetails;
                          return (
                            <Fragment key={item.index}>
                              <span
                                data-cy={`campaignStep-${item.step}`}
                                aria-current={isActive ? "step" : undefined}
                                onClick={() => {
                                  if (isLink) {
                                    handleGoToTab(item.href);
                                  }
                                }}
                                className={cn(classes.item, "mob-item", {
                                  [classes.itemActive]: isActive,
                                  "display-none": nonActive,
                                  [classes.displayNone]: isMobile && nonActive,
                                  [classes.itemCanBeActive]: isLink && canBeActive,
                                })}
                              >
                                {headerWithSkipLogic(isActive, canBeActive, item, forbidDetails)}
                                <Box
                                  key={`${item.title}-${item.index}`}
                                  className={cn(classes.tabWrapper, { [classes.mobTabWrapper]: isMobile })}
                                >
                                  <Typography
                                    className={cn(classes.tab, {
                                      [classes.activeTab]: (isActive || canBeActive) && !forbidDetails,
                                      [classes.link]: isLink,
                                    })}
                                  >
                                    {item.title}
                                  </Typography>
                                  <StepDots totalSteps={tabs.length} currentStep={activeTabIndex} isMobile={isMobile} />
                                </Box>
                              </span>
                              {item.index < tabs.length ? (
                                <Box
                                  key={`seperator-${item.index}`}
                                  className={cn(classes.separator, "mob-separator", {
                                    [classes.disabled]: isLoading || !canBeActive,
                                    [classes.displayNone]: isMobile,
                                  })}
                                />
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item className={cn(classes.navigationHelp, "flex items-center justify-end")}>
                      <CopilotLauncher />
                    </Grid>
                  </Box>
                </Grid>
              </Box>
            )}
          </IfElse>
          {!isFlyerBuilderPage && !isSubmitPage && tabs.length > 0 && (
            <EddmStepBillingGate clientId={activeClientId} surface="eddm-step" />
          )}
        </Box>
      </IfElse>
    </>
  );
};
export default withStyles(style as any)(withRouter(Header));

interface StepDotsProps {
  totalSteps: number;
  currentStep: number;
  isMobile: boolean;
}

const StepDots = ({ totalSteps, currentStep, isMobile }: StepDotsProps) => {
  const classes = useStyles();
  if (!isMobile) return null;
  return (
    <Box className={cn(classes.dotsContainer)}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <span key={i + 1} className={cn(classes.dot, { [classes.activeDot]: i + 1 === currentStep })} />
      ))}
    </Box>
  );
};

// DM-only step dots — Tailwind + tokenized dark, mirroring EDDMHeader's StepDots
// so the mobile submit topbar matches the other EDDM steps. The legacy
// `StepDots` above (makeStyles) is still used by the non-DM branch untouched.
const DMStepDots = ({ totalSteps, currentStep, isMobile }: StepDotsProps) => {
  if (!isMobile) return null;
  return (
    <div className="flex items-center justify-center">
      {Array.from({ length: totalSteps }, (_, i) => (
        <span
          key={i + 1}
          className={cn(
            "mx-1 rounded-full",
            i + 1 === currentStep ? "h-2.5 w-2.5 bg-black dark:bg-foreground" : "h-2 w-2 bg-[#d3d3d3] dark:bg-slate-600"
          )}
        />
      ))}
    </div>
  );
};
